import { Modal } from 'antd'
import {
  DocumentDrawing,
  DocumentRegion,
} from 'features/libra/document/LibraDocumentContext'
import { useDocumentDrawing } from 'features/libra/drawing/useDocumentDrawing'
import { ZONE_COLORS } from 'features/libra/stepComponents/zoneAnalysis/aiSuggestedZones'
import {
  zoneBoxLabel,
  ZoneBoxTarget,
  ZoneChangeGuard,
  ZoneParams,
  ZoneRectangle,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  buildZonePixelPolicy,
  validateZoneGeometryChanges,
} from 'features/libra/stepComponents/zoneAnalysis/zoneGeometryValidation'
import {
  appendZoneBox,
  removeZoneBox,
  replaceZoneBox,
} from 'features/libra/stepComponents/zoneAnalysis/zoneMutations'
import { useGeckoNotifications } from 'features/notifications'
import isEqual from 'lodash/isEqual'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { v4 } from 'uuid'

import { projectZoneGeometry, sameZoneGeometry } from './zoneEditorGeometry'

type Options = {
  zones: ZoneParams[]
  mutationBaselineZones?: readonly ZoneParams[]
  onAddZone: (zone: ZoneParams) => void
  onChangeZone: (
    zoneId: string,
    updates: Partial<ZoneParams>,
    guard?: ZoneChangeGuard
  ) => void
  onRemoveZone: (zoneId: string) => void
  unitId: string
  readonly: boolean
  isLoading: boolean
  drawingResetKey?: number
}

type RemoveRectangleConfirmation = {
  target: ZoneBoxTarget
  sourceImageId?: string
  bounds: ZoneRectangle
  boxRevision: number
  resetKey?: number
  handle?: ReturnType<typeof Modal.confirm>
}

type ZoneBoxAppendContext = {
  target: ZoneBoxTarget
  imageId: string
  boxRevision: number
}

function visualRegionId(target: ZoneBoxTarget, revision: number): string {
  return JSON.stringify([target.zoneId, target.boxIndex, revision])
}

export const useZoneEditorInteraction = ({
  zones,
  mutationBaselineZones,
  onAddZone,
  onChangeZone,
  onRemoveZone,
  unitId,
  readonly,
  isLoading,
  drawingResetKey,
}: Options) => {
  const { errorNotification, infoNotification } = useGeckoNotifications()

  // The backend omits image size, so only the browser measurement can scale AI bounds.
  const [imageNaturalSize, setImageNaturalSize] = useState<{
    documentId: string
    width: number
    height: number
  }>()
  const [selection, setSelection] = useState<{
    zoneId?: string
    boxIndex?: number
  }>({})
  const [boxRevision, setBoxRevision] = useState(0)
  const boxRevisionRef = useRef(0)
  const selectionRef = useRef(selection)
  const regionTargetsRef = useRef(new Map<string, ZoneBoxTarget>())
  const rowRefs = useRef(new Map<string, HTMLDivElement>())
  // Keep synchronous receipts/confirmation current; owners still apply by ID.
  const zonesRef = useRef(zones)
  zonesRef.current = zones
  const zoneStructureRef = useRef(projectZoneGeometry(zones))
  const currentZoneStructure = useMemo(
    () => projectZoneGeometry(zones),
    [zones]
  )
  const readonlyRef = useRef(readonly)
  readonlyRef.current = readonly
  const imageNaturalSizeRef = useRef(imageNaturalSize)
  imageNaturalSizeRef.current = imageNaturalSize
  const drawingResetKeyRef = useRef(drawingResetKey)
  drawingResetKeyRef.current = drawingResetKey
  const removeRectangleConfirmationRef = useRef<
    RemoveRectangleConfirmation | undefined
  >(undefined)
  selectionRef.current = selection

  const selectedZoneId = selection.zoneId
  const selectedBoxTarget = useMemo(
    () =>
      selection.zoneId && selection.boxIndex !== undefined
        ? { zoneId: selection.zoneId, boxIndex: selection.boxIndex }
        : undefined,
    [selection.boxIndex, selection.zoneId]
  )

  const invalidateRemoveRectangleConfirmation = useCallback(() => {
    const confirmation = removeRectangleConfirmationRef.current
    removeRectangleConfirmationRef.current = undefined
    confirmation?.handle?.destroy()
  }, [])

  const bumpBoxRevision = useCallback(() => {
    const next = boxRevisionRef.current + 1
    boxRevisionRef.current = next
    setBoxRevision(next)
    invalidateRemoveRectangleConfirmation()
  }, [invalidateRemoveRectangleConfirmation])

  useLayoutEffect(() => {
    if (isEqual(zoneStructureRef.current, currentZoneStructure)) return
    zoneStructureRef.current = currentZoneStructure
    bumpBoxRevision()
  }, [bumpBoxRevision, currentZoneStructure, zones])

  const setZoneSelection = useCallback(
    (zoneId?: string, boxIndex?: number) => {
      const next = zoneId ? { zoneId, boxIndex } : {}
      if (isEqual(selectionRef.current, next)) return
      selectionRef.current = next
      setSelection(next)
      invalidateRemoveRectangleConfirmation()
    },
    [invalidateRemoveRectangleConfirmation]
  )

  useLayoutEffect(
    () => invalidateRemoveRectangleConfirmation,
    [drawingResetKey, invalidateRemoveRectangleConfirmation, readonly]
  )
  useLayoutEffect(() => {
    const confirmation = removeRectangleConfirmationRef.current
    if (!confirmation) return
    const currentZone = zones.find(
      (zone) => zone.id === confirmation.target.zoneId
    )
    if (
      !currentZone ||
      boxRevisionRef.current !== confirmation.boxRevision ||
      !isEqual(selectionRef.current, confirmation.target) ||
      currentZone.zone_reference_image_id !== confirmation.sourceImageId ||
      !isEqual(
        currentZone.drawing_bounds?.[confirmation.target.boxIndex],
        confirmation.bounds
      )
    )
      invalidateRemoveRectangleConfirmation()
  }, [boxRevision, invalidateRemoveRectangleConfirmation, zones])

  const applyZoneOperation = useCallback(
    (
      next: ZoneParams[],
      apply: () => void,
      forceValidateIds?: readonly string[]
    ) => {
      if (readonlyRef.current) return false
      const current = zonesRef.current
      const measured = imageNaturalSizeRef.current
      const operationGeometryError = validateZoneGeometryChanges({
        baselineZones: current,
        proposedZones: next,
        measuredImage: measured,
        forceValidateIds,
      })
      if (operationGeometryError) {
        errorNotification(operationGeometryError)
        return false
      }
      if (mutationBaselineZones) {
        const savedGeometryError = validateZoneGeometryChanges({
          baselineZones: mutationBaselineZones,
          proposedZones: next.filter((zone) => !zone.ai_suggested),
          measuredImage: measured,
          forceValidateIds,
        })
        if (savedGeometryError) {
          errorNotification(savedGeometryError)
          return false
        }
      }
      if (!sameZoneGeometry(current, next)) {
        zoneStructureRef.current = projectZoneGeometry(next)
        bumpBoxRevision()
      }
      zonesRef.current = next
      apply()
      return true
    },
    [bumpBoxRevision, errorNotification, mutationBaselineZones]
  )

  const handleRemoveRectangle = useCallback(
    (target: ZoneBoxTarget, expectedRevision: number) => {
      if (readonlyRef.current || boxRevisionRef.current !== expectedRevision)
        return
      const currentZone = zonesRef.current.find(
        (zone) => zone.id === target.zoneId
      )
      const bounds = currentZone?.drawing_bounds?.[target.boxIndex]
      if (!currentZone || !bounds) return
      setZoneSelection(target.zoneId, target.boxIndex)
      invalidateRemoveRectangleConfirmation()
      const confirmation: RemoveRectangleConfirmation = {
        target,
        sourceImageId: currentZone.zone_reference_image_id,
        bounds: { ...bounds },
        boxRevision: boxRevisionRef.current,
        resetKey: drawingResetKeyRef.current,
      }
      removeRectangleConfirmationRef.current = confirmation
      confirmation.handle = Modal.confirm({
        title: 'Remove rectangle?',
        content:
          'This removes only the rectangle. The zone, its answers, and its source image will remain.',
        okText: 'Remove rectangle',
        okButtonProps: { danger: true },
        cancelText: 'Keep rectangle',
        onOk: () => {
          if (
            removeRectangleConfirmationRef.current !== confirmation ||
            readonlyRef.current ||
            drawingResetKeyRef.current !== confirmation.resetKey
          )
            return
          removeRectangleConfirmationRef.current = undefined
          const current = zonesRef.current
          const zone = current.find((item) => item.id === target.zoneId)
          if (
            !zone ||
            zone.zone_reference_image_id !== confirmation.sourceImageId ||
            boxRevisionRef.current !== confirmation.boxRevision ||
            !isEqual(selectionRef.current, target) ||
            !isEqual(
              zone.drawing_bounds?.[target.boxIndex],
              confirmation.bounds
            )
          )
            return
          const updated = removeZoneBox(current, target)
          const remaining =
            updated.find((item) => item.id === target.zoneId)?.drawing_bounds ??
            []
          applyZoneOperation(updated, () => {
            onChangeZone(
              target.zoneId,
              { drawing_bounds: remaining },
              {
                zone_reference_image_id: confirmation.sourceImageId,
                drawing_bounds: zone.drawing_bounds ?? [],
              }
            )
            setZoneSelection(
              target.zoneId,
              remaining.length
                ? Math.min(target.boxIndex, remaining.length - 1)
                : undefined
            )
          })
        },
        onCancel: () => {
          if (removeRectangleConfirmationRef.current === confirmation)
            removeRectangleConfirmationRef.current = undefined
        },
      })
    },
    [
      applyZoneOperation,
      invalidateRemoveRectangleConfirmation,
      onChangeZone,
      setZoneSelection,
    ]
  )

  const handleRemoveRegion = useCallback(
    (regionId: string) => {
      if (boxRevisionRef.current !== boxRevision) return
      const target = regionTargetsRef.current.get(regionId)
      if (target) handleRemoveRectangle(target, boxRevision)
    },
    [boxRevision, handleRemoveRectangle]
  )

  const drawingRegions = useMemo(() => {
    const targets = new Map<string, ZoneBoxTarget>()
    const regions: DocumentRegion[] = []
    zones.forEach((zone) => {
      if (!zone.zone_reference_image_id) return
      zone.drawing_bounds?.forEach((bounds, boxIndex) => {
        const target = { zoneId: zone.id, boxIndex }
        const id = visualRegionId(target, boxRevision)
        targets.set(id, target)
        regions.push({
          id,
          title: zoneBoxLabel(zone.title, boxIndex),
          color: zone.color,
          ai_suggested: zone.ai_suggested,
          zone_reference_image_id: zone.zone_reference_image_id,
          drawing_bounds: {
            x_start: bounds.x_start,
            y_start: bounds.y_start,
            x_end: bounds.x_end,
            y_end: bounds.y_end,
          },
          readOnly: readonly,
        })
      })
    })
    regionTargetsRef.current = targets
    return regions
  }, [boxRevision, readonly, zones])

  const referenceDocumentIds = useMemo(
    () =>
      [...new Set(zones.map((zone) => zone.zone_reference_image_id))].filter(
        (id): id is string => Boolean(id)
      ),
    [zones]
  )

  const selectedRegionId =
    selectedBoxTarget && visualRegionId(selectedBoxTarget, boxRevision)

  const handleUpdateRectangle = useCallback(
    (
      regionId: string,
      drawingBounds: DocumentDrawing['drawing_bounds'],
      sourceImageId?: string
    ) => {
      const target = regionTargetsRef.current.get(regionId)
      if (
        readonlyRef.current ||
        boxRevisionRef.current !== boxRevision ||
        !target ||
        !isEqual(selectionRef.current, target)
      )
        return false
      const current = zonesRef.current
      const existing = current.find((zone) => zone.id === target.zoneId)
      const imageId = sourceImageId ?? existing?.zone_reference_image_id
      if (!imageId || imageNaturalSizeRef.current?.documentId !== imageId) {
        infoNotification(
          'The reference image must finish loading before editing a rectangle.'
        )
        return false
      }
      if (!existing) return false
      if (existing.zone_reference_image_id !== imageId) {
        errorNotification(
          'A rectangle can only be redrawn on its current reference image.'
        )
        return false
      }
      const next = replaceZoneBox(current, target, drawingBounds)
      const nextBounds = next.find(
        (zone) => zone.id === target.zoneId
      )?.drawing_bounds
      return applyZoneOperation(next, () =>
        onChangeZone(
          target.zoneId,
          { drawing_bounds: nextBounds },
          {
            zone_reference_image_id: existing.zone_reference_image_id,
            drawing_bounds: existing.drawing_bounds ?? [],
          }
        )
      )
    },
    [
      applyZoneOperation,
      boxRevision,
      errorNotification,
      infoNotification,
      onChangeZone,
    ]
  )

  const handleDraw = useCallback(
    (
      drawing: DocumentDrawing,
      regionId?: string,
      appendContext?: ZoneBoxAppendContext
    ) => {
      if (readonlyRef.current || boxRevisionRef.current !== boxRevision)
        return undefined
      if (
        !drawing.zone_reference_image_id ||
        imageNaturalSizeRef.current?.documentId !==
          drawing.zone_reference_image_id
      ) {
        infoNotification(
          'The reference image must finish loading before drawing.'
        )
        return undefined
      }
      if (regionId) {
        return handleUpdateRectangle(
          regionId,
          drawing.drawing_bounds,
          drawing.zone_reference_image_id
        )
          ? regionId
          : undefined
      }

      const current = zonesRef.current
      if (appendContext) {
        const { target, imageId, boxRevision: commandRevision } = appendContext
        if (
          commandRevision !== boxRevisionRef.current ||
          imageId !== drawing.zone_reference_image_id
        )
          return undefined
        const zone = current.find((item) => item.id === target.zoneId)
        if (
          zone?.zone_reference_image_id !== imageId ||
          (zone.drawing_bounds?.length ?? 0) !== target.boxIndex
        )
          return undefined
        const next = appendZoneBox(
          current,
          target.zoneId,
          drawing.drawing_bounds
        )
        const nextBounds = next.find(
          (item) => item.id === target.zoneId
        )?.drawing_bounds
        if (
          !applyZoneOperation(next, () => {
            onChangeZone(
              target.zoneId,
              { drawing_bounds: nextBounds },
              {
                zone_reference_image_id: zone.zone_reference_image_id,
                drawing_bounds: zone.drawing_bounds ?? [],
              }
            )
            setZoneSelection(target.zoneId, target.boxIndex)
          })
        )
          return undefined
        return visualRegionId(target, boxRevisionRef.current)
      }

      const id = v4().substring(0, 6)
      const zone: ZoneParams = {
        id,
        title: `Zone ${current.filter((item) => item.zone_reference_image_id === drawing.zone_reference_image_id).length + 1}`,
        ...drawing,
        color: ZONE_COLORS[current.length % ZONE_COLORS.length],
        geometry: 'RECTANGLE',
        drawing_bounds: [drawing.drawing_bounds],
        damage_mechanisms: [],
        zone_recommendations: [],
        assumptions: [],
      }
      if (!applyZoneOperation([...current, zone], () => onAddZone(zone)))
        return undefined
      setZoneSelection(id, 0)
      return visualRegionId({ zoneId: id, boxIndex: 0 }, boxRevisionRef.current)
    },
    [
      applyZoneOperation,
      boxRevision,
      handleUpdateRectangle,
      infoNotification,
      onAddZone,
      onChangeZone,
      setZoneSelection,
    ]
  )

  const handleSelectRegion = useCallback(
    (regionId: string | undefined) => {
      if (!regionId) {
        setZoneSelection()
        return
      }
      const target = regionTargetsRef.current.get(regionId)
      if (!target) return
      setZoneSelection(target.zoneId, target.boxIndex)
    },
    [setZoneSelection]
  )

  const drawing = useDocumentDrawing<ZoneBoxAppendContext>({
    regions: drawingRegions,
    unitId,
    disabled: readonly,
    isLoading,
    onDraw: handleDraw,
    onImageNaturalSize: setImageNaturalSize,
    selectedRegionId,
    onSelectRegion: handleSelectRegion,
    onUpdateRegion: handleUpdateRectangle,
    onRemoveRegion: handleRemoveRegion,
    referenceDocumentIds,
    interactionRevision: boxRevision,
    drawingResetKey,
  })
  const drawingReportsRef = useRef(drawing.reports)
  drawingReportsRef.current = drawing.reports
  const drawingTargetRef = useRef(drawing.viewProps.target)
  drawingTargetRef.current = drawing.viewProps.target
  const { selectedDocument, openDocument, showRegion } = drawing
  const cancelDrawing = drawing.cancelDrawing
  const startDrawingForDocument = drawing.startDrawingForDocument
  const drawingTarget = drawing.viewProps.target
  const drawingCommand = drawing.command
  const drawingSourceTarget = useMemo(
    () =>
      drawingTarget?.url
        ? { documentId: drawingTarget.documentId, url: drawingTarget.url }
        : undefined,
    [drawingTarget?.documentId, drawingTarget?.url]
  )
  const drawingSourceTargetRef = useRef(drawingSourceTarget)
  useLayoutEffect(() => {
    const previous = drawingSourceTargetRef.current
    drawingSourceTargetRef.current = drawingSourceTarget
    if (!previous) return
    if (!drawingSourceTarget) {
      // Losing the currently selected, usable source invalidates captured
      // edits. A navigation to another image with no target yet is expected.
      if (previous.documentId === selectedDocument?.id) bumpBoxRevision()
      return
    }
    // A new image binding is expected while navigating. Only a changed URL
    // for the same image replaces the captured source.
    if (previous.documentId !== drawingSourceTarget.documentId) return
    if (previous.url !== drawingSourceTarget.url) bumpBoxRevision()
  }, [bumpBoxRevision, drawingSourceTarget, selectedDocument?.id])

  const drawingPixelPolicy = useMemo(() => {
    if (
      !drawingTarget ||
      !imageNaturalSize ||
      imageNaturalSize.documentId !== drawingTarget.documentId
    )
      return undefined
    const excludeTarget = drawingCommand?.replacementRegionId
      ? regionTargetsRef.current.get(drawingCommand.replacementRegionId)
      : drawingCommand
        ? undefined
        : selectedBoxTarget
    return buildZonePixelPolicy({
      documentId: drawingTarget.documentId,
      zones,
      measuredImage: imageNaturalSize,
      excludeTarget,
    })
  }, [
    drawingCommand,
    drawingTarget,
    imageNaturalSize,
    selectedBoxTarget,
    zones,
  ])
  const handleSelectZone = useCallback(
    (zone: ZoneParams) => {
      const boxCount = zone.drawing_bounds?.length ?? 0
      setZoneSelection(zone.id, boxCount > 0 ? 0 : undefined)
      if (!zone.zone_reference_image_id) return
      const region = drawingRegions.find(
        (item) =>
          regionTargetsRef.current.get(item.id)?.zoneId === zone.id &&
          regionTargetsRef.current.get(item.id)?.boxIndex === 0
      )
      if (region) showRegion(region)
      else openDocument(zone.zone_reference_image_id)
    },
    [drawingRegions, openDocument, setZoneSelection, showRegion]
  )

  const handleSelectBox = useCallback(
    (zone: ZoneParams, boxIndex: number, expectedRevision: number) => {
      if (boxRevisionRef.current !== expectedRevision) return
      setZoneSelection(zone.id, boxIndex)
      const target = { zoneId: zone.id, boxIndex }
      const region = drawingRegions.find((item) =>
        isEqual(regionTargetsRef.current.get(item.id), target)
      )
      if (region) showRegion(region)
      else if (zone.zone_reference_image_id)
        openDocument(zone.zone_reference_image_id)
    },
    [drawingRegions, openDocument, setZoneSelection, showRegion]
  )

  const handleRedrawBox = useCallback(
    (zone: ZoneParams, expectedRevision: number) => {
      if (boxRevisionRef.current !== expectedRevision) return
      const target =
        selectedBoxTarget?.zoneId === zone.id
          ? selectedBoxTarget
          : zone.drawing_bounds?.length
            ? { zoneId: zone.id, boxIndex: 0 }
            : undefined
      if (!target) return
      setZoneSelection(target.zoneId, target.boxIndex)
      const region = drawingRegions.find((item) =>
        isEqual(regionTargetsRef.current.get(item.id), target)
      )
      if (region) showRegion(region, true)
      else if (zone.zone_reference_image_id)
        openDocument(zone.zone_reference_image_id)
    },
    [
      drawingRegions,
      openDocument,
      selectedBoxTarget,
      setZoneSelection,
      showRegion,
    ]
  )

  const handleQueueBoxAppend = useCallback(
    (zone: ZoneParams, expectedRevision: number) => {
      if (readonlyRef.current || boxRevisionRef.current !== expectedRevision)
        return
      if (!zone.zone_reference_image_id) {
        infoNotification('A source image is required before adding a box.')
        return
      }
      const boxIndex = zone.drawing_bounds?.length ?? 0
      setZoneSelection(zone.id, boxIndex > 0 ? boxIndex - 1 : undefined)
      const context = {
        target: { zoneId: zone.id, boxIndex },
        imageId: zone.zone_reference_image_id,
        boxRevision: boxRevisionRef.current,
      }
      const selectedRegionId =
        boxIndex > 0
          ? visualRegionId(
              { zoneId: zone.id, boxIndex: boxIndex - 1 },
              boxRevisionRef.current
            )
          : undefined
      startDrawingForDocument(
        zone.zone_reference_image_id,
        context,
        selectedRegionId
      )
    },
    [infoNotification, setZoneSelection, startDrawingForDocument]
  )

  useEffect(() => {
    if (selectedZoneId)
      rowRefs.current.get(selectedZoneId)?.scrollIntoView({ block: 'nearest' })
  }, [selectedZoneId])

  useEffect(() => {
    if (
      selectedZoneId &&
      !zones.some(
        (zone) => zone.id === selectedZoneId && zone.zone_reference_image_id
      )
    )
      setZoneSelection()
  }, [selectedZoneId, setZoneSelection, zones])

  const handleDeleteZone = useCallback(
    (zoneId: string) => {
      if (readonlyRef.current) return
      cancelDrawing()
      applyZoneOperation(
        zonesRef.current.filter((zone) => zone.id !== zoneId),
        () => onRemoveZone(zoneId)
      )
      if (selectedZoneId === zoneId) setZoneSelection()
    },
    [
      applyZoneOperation,
      cancelDrawing,
      onRemoveZone,
      selectedZoneId,
      setZoneSelection,
    ]
  )

  const handleUpdateZone = useCallback(
    (zoneId: string, updates: Partial<ZoneParams>) => {
      if (readonlyRef.current) return
      if (
        'id' in updates ||
        'drawing_bounds' in updates ||
        'zone_reference_image_id' in updates ||
        'bounds' in updates ||
        'ai_initial_bounds' in updates ||
        'ai_suggested' in updates
      ) {
        errorNotification(
          'Rectangle and source changes must use the drawing editor.'
        )
        return
      }
      const next = zonesRef.current.map((zone) =>
        zone.id === zoneId ? { ...zone, ...updates } : zone
      )
      applyZoneOperation(next, () => onChangeZone(zoneId, updates))
    },
    [applyZoneOperation, errorNotification, onChangeZone]
  )

  const registerRow = useCallback(
    (zoneId: string, element: HTMLDivElement | null) => {
      if (element) rowRefs.current.set(zoneId, element)
      else rowRefs.current.delete(zoneId)
    },
    []
  )
  const replaceZonesFromAI = useCallback((nextZones: ZoneParams[]) => {
    zoneStructureRef.current = projectZoneGeometry(nextZones)
    zonesRef.current = nextZones
  }, [])

  return {
    applyZoneOperation,
    boxRevision,
    drawing,
    drawingPixelPolicy,
    drawingReportsRef,
    drawingResetKeyRef,
    drawingTargetRef,
    handleDeleteZone,
    handleQueueBoxAppend,
    handleRedrawBox,
    handleRemoveRectangle,
    handleSelectBox,
    handleSelectZone,
    handleUpdateZone,
    imageNaturalSize,
    imageNaturalSizeRef,
    openDocument,
    readonlyRef,
    registerRow,
    replaceZonesFromAI,
    selectedBoxTarget,
    selectedDocument,
    selectedZoneId,
    setZoneSelection,
    zonesRef,
  }
}
